import React, { useState } from 'react';
import { LogIn, Mail, Lock } from 'lucide-react';
import { signIn } from '../lib/auth';
import AuthLayout from '../components/auth/AuthLayout';
import AuthInput from '../components/auth/AuthInput';
import AuthButton from '../components/auth/AuthButton';
import AuthError from '../components/auth/AuthError';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signIn(formData.email, formData.password);
      
      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg';
      successMessage.innerHTML = `
        <div class="flex flex-col">
          <span class="font-medium">Login successful!</span>
          <span class="text-sm mt-1">Redirecting to dashboard...</span>
        </div>
      `;
      document.body.appendChild(successMessage);

      // Redirect after delay
      setTimeout(() => {
        document.body.removeChild(successMessage);
        window.location.href = '/dashboard';
      }, 1500);
    } catch (err) {
      setError(
        err instanceof Error 
          ? err.message.includes('Invalid login') 
            ? 'Invalid email or password'
            : err.message
          : 'An error occurred during login'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="inline-flex flex-col items-center justify-center mb-6">
              <div className="logo-container w-12 h-12 flex items-center justify-center mb-2">
                <span className="logo-text text-2xl">S</span>
              </div>
              <span className="text-2xl font-bold">Serial Suite</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground">
              Sign in to your account to continue
            </p>
          </div>

          <AuthError message={error} />

          <form onSubmit={handleSubmit} className="space-y-6">
            <AuthInput
              label="Email address"
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              icon={Mail}
              placeholder="name@company.com"
              required
            />

            <AuthInput
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              icon={Lock}
              placeholder="••••••••"
              required
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                  className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-primary"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-muted-foreground">
                  Remember me
                </label>
              </div>
              <Button variant="link" className="text-sm">
                Forgot password?
              </Button>
            </div>

            <AuthButton type="submit" loading={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </AuthButton>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Button
              variant="link"
              className="font-medium"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/register';
              }}
            >
              Create one now
            </Button>
          </p>
        </div>
      </div>

      {/* Right Column - Cover Image */}
      <div className="hidden lg:block relative w-0 flex-1">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&q=80"
          alt="Service professional at work"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-gray-900/10" />
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <blockquote className="space-y-2">
            <p className="text-lg font-medium">
              "Serial Suite has transformed how we manage our service business. The efficiency gains have been remarkable."
            </p>
            <footer className="text-sm">
              Sarah Lopez • Mason Luxury Cleaning
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Login;